import type { PlaceLocationRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { ApiErrorMessage, NoddiAsyncSelect, type SelectOption } from 'noddi-ui-web';
import { commonTexts } from 'noddi-util';
import { useEffect, useRef, useState } from 'react';
import { renderAddressSuggestionOption, useAddressSuggestions } from '../Address/addressSuggestions';

export type PlaceSearchTranslations = {
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
};

type PlaceSearchProps = {
  onPlaceSelected: (location: PlaceLocationRecord) => void;
  onClear?: () => void;
  className?: string;
  translations?: PlaceSearchTranslations;
};

export const PlaceSearch = ({ onPlaceSelected, onClear, className, translations }: PlaceSearchProps) => {
  const t = translations ?? commonTexts.placeSearch;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState<SelectOption<string> | undefined>();
  // Keep the latest callback without making the notification effect re-run for parent re-renders.
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  // A selected place can trigger multiple renders while the query settles; notify the parent once per place id.
  const lastNotifiedPlaceIdRef = useRef<string | null>(null);
  const selectedPlaceId = selectedOption?.value ?? null;

  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  const { addressSuggestionOptions, isAddressSuggestionsLoading, addressSuggestionsError } =
    useAddressSuggestions(searchTerm);

  const {
    data: placeLocation,
    isLoading: isLocationLoading,
    error: placeLocationError
  } = noddiAsync.useGet({
    type: URLKeys.getPlaceLocation,
    input: { placeId: selectedPlaceId ?? '' },
    queryConfig: {
      enabled: selectedPlaceId !== null
    }
  });

  useEffect(() => {
    if (!selectedPlaceId) {
      lastNotifiedPlaceIdRef.current = null;
      return;
    }

    if (
      !placeLocation ||
      placeLocation.placeId !== selectedPlaceId ||
      lastNotifiedPlaceIdRef.current === selectedPlaceId
    ) {
      return;
    }

    lastNotifiedPlaceIdRef.current = selectedPlaceId;

    // Forward the resolved place only after the location query matches the latest selection.
    onPlaceSelectedRef.current({
      placeId: selectedPlaceId,
      latitude: placeLocation.latitude,
      longitude: placeLocation.longitude,
      displayName: placeLocation.displayName,
      addressInfo: placeLocation.addressInfo
    });
  }, [placeLocation, selectedPlaceId]);

  const handleSelectionChange = (option: SelectOption<string> | undefined) => {
    if (!option) {
      setSelectedOption(undefined);
      lastNotifiedPlaceIdRef.current = null;
      // Clearing the input should also clear any map marker/state derived from the previous selection.
      onClear?.();
      return;
    }

    setSelectedOption(option);
  };

  return (
    <div className={cn('flex w-72 flex-col gap-2', className)}>
      <NoddiAsyncSelect
        placeholder={t.placeholder}
        searchPlaceholder={t.searchPlaceholder}
        emptyMessage={t.emptyMessage}
        options={addressSuggestionOptions}
        selectedOption={selectedOption}
        onSelectionChange={handleSelectionChange}
        onSearch={setSearchTerm}
        isLoading={isAddressSuggestionsLoading || isLocationLoading}
        renderOption={renderAddressSuggestionOption}
        startIcon='MapPin'
        noOptionsUnlessSearched
      />
      {(addressSuggestionsError || placeLocationError) && (
        <ApiErrorMessage error={[addressSuggestionsError, placeLocationError]} />
      )}
    </div>
  );
};
